/**
 * Tools Hub — Manifest-driven tool discovery grid
 *
 * Redesign Phase 1.1 — Anti-orphan guarantee implementation.
 *
 * Every TOOLS_MANIFEST entry visible to the user's role is rendered as a
 * ToolTile, grouped by NavGroup. Entries with non-matching roles render as
 * locked/dimmed tiles (not hidden), providing discoverability.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  TOOLS_MANIFEST,
  ToolManifestEntry,
  WorkspaceRole,
  NavGroup,
} from '@/config/toolsManifest';
import { isFeatureEnabled } from '@/config/featureFlags';
import { ToolTile } from '@/components/ui/ToolTile';
import { BRAND_NAME } from '@/config/brand';

// Icons for different groups (lucide-react icons)
import {
  LayoutDashboard,
  FolderOpen,
  Search,
  FileOutput,
  BrainCircuit,
  Settings,
  Lock,
  LucideIcon,
} from 'lucide-react';

// Mapping from NavGroup to icon component
const GROUP_ICONS: Record<NavGroup, LucideIcon> = {
  work_desk: LayoutDashboard,
  cases: FolderOpen,
  review: Search,
  deliverables: FileOutput,
  intelligence: BrainCircuit,
  admin: Settings,
};

// Human-friendly group labels
const GROUP_LABELS: Record<NavGroup, string> = {
  work_desk: 'Work Desk',
  cases: 'Cases',
  review: 'Review',
  deliverables: 'Deliverables',
  intelligence: 'Intelligence',
  admin: 'Admin',
};

// Order for group display
const GROUP_ORDER: NavGroup[] = [
  'work_desk',
  'cases',
  'review',
  'deliverables',
  'intelligence',
  'admin',
];

interface ToolsHubProps {
  /** Override role for testing/storybook */
  roleOverride?: WorkspaceRole;
}

/**
 * Determine the effective workspace role from Clerk user metadata.
 * Falls back to 'analyst' if no role is found.
 */
function useWorkspaceRole(roleOverride?: WorkspaceRole): WorkspaceRole {
  const { user } = useUser();

  if (roleOverride) {
    return roleOverride;
  }

  // Check Clerk publicMetadata for role
  const metadataRole = user?.publicMetadata?.role as WorkspaceRole | undefined;
  if (metadataRole) {
    return metadataRole;
  }

  // Default role for users without explicit role assignment
  return 'analyst';
}

/**
 * Check if the user has access to a manifest entry based on their role.
 */
function hasRoleAccess(entry: ToolManifestEntry, userRole: WorkspaceRole): boolean {
  return entry.roles.includes(userRole);
}

/**
 * Get appropriate icon for a tool entry based on its group and synonyms.
 * Returns a Lucide icon class name.
 */
function getToolIcon(entry: ToolManifestEntry): LucideIcon {
  // Default to the group icon
  const groupIcon = GROUP_ICONS[entry.group];

  // Could extend here to map specific tool ids to specific icons
  // For now, use group icon as default
  return groupIcon;
}

/**
 * Tools Hub page component.
 */
export default function ToolsHub({ roleOverride }: ToolsHubProps): React.ReactElement | null {
  const navigate = useNavigate();
  const userRole = useWorkspaceRole(roleOverride);

  // Feature flag gate
  if (!isFeatureEnabled('TOOLS_HUB')) {
    return null;
  }

  // Filter out paletteOnly entries (they're only for CommandPalette)
  // and entries that require a case (they're shown in case workspace context)
  const hubEntries = TOOLS_MANIFEST.filter(
    (entry) => !entry.paletteOnly && !entry.needsCase
  );

  // Group entries by NavGroup
  const entriesByGroup = React.useMemo(() => {
    const grouped: Record<NavGroup, ToolManifestEntry[]> = {
      work_desk: [],
      cases: [],
      review: [],
      deliverables: [],
      intelligence: [],
      admin: [],
    };

    hubEntries.forEach((entry) => {
      grouped[entry.group].push(entry);
    });

    return grouped;
  }, [hubEntries]);

  /**
   * Handle tile click - navigate to route or dispatch surface open.
   */
  const handleTileClick = (entry: ToolManifestEntry) => {
    // If entry has a route, navigate to it
    if (entry.route) {
      navigate(entry.route);
      return;
    }

    // If entry has capability.surface but no route, dispatch surface open
    if (entry.capability?.surface) {
      // Dispatch custom event for surface open
      const event = new CustomEvent('tools:open-surface', {
        detail: {
          surface: entry.capability.surface,
          backend: entry.capability.backend,
          entry,
        },
      });
      window.dispatchEvent(event);
    }
  };

  return (
    <div
      className="min-h-screen p-6 md:p-8"
      style={{ backgroundColor: 'var(--color-bg-base)' }}
    >
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-10">
        <h1
          className="text-3xl md:text-4xl mb-2"
          style={{
            fontFamily: '"DM Serif Display", Georgia, serif',
            color: 'var(--color-text-primary)',
          }}
        >
          Tools Hub
        </h1>
        <p
          className="text-base"
          style={{
            fontFamily: 'Outfit, system-ui, sans-serif',
            color: 'var(--color-text-muted)',
          }}
        >
          All {BRAND_NAME} capabilities — accessible from one place.
        </p>
      </div>

      {/* Groups */}
      <div className="max-w-7xl mx-auto space-y-12">
        {GROUP_ORDER.map((group) => {
          const entries = entriesByGroup[group];
          if (entries.length === 0) return null;

          const GroupIcon = GROUP_ICONS[group];
          const hasAccessibleTools = entries.some((e) => hasRoleAccess(e, userRole));

          return (
            <section key={group}>
              {/* Group Header */}
              <div className="flex items-center gap-3 mb-5">
                <GroupIcon
                  className="w-5 h-5"
                  style={{ color: 'var(--color-brand)' }}
                />
                <h2
                  className="text-lg md:text-xl"
                  style={{
                    fontFamily: '"DM Serif Display", Georgia, serif',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {GROUP_LABELS[group]}
                </h2>
              </div>

              {/* Tiles Grid */}
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                }}
              >
                {entries.map((entry) => {
                  const hasAccess = hasRoleAccess(entry, userRole);
                  const ToolIcon = getToolIcon(entry);

                  return (
                    <ToolTile
                      key={entry.id}
                      title={entry.label}
                      description={entry.synonyms?.join(' · ') || ''}
                      icon={
                        hasAccess ? (
                          <ToolIcon className="w-6 h-6" />
                        ) : (
                          <Lock className="w-6 h-6" />
                        )
                      }
                      disabled={!hasAccess}
                      onClick={() => hasAccess && handleTileClick(entry)}
                      className="transition-all duration-200"
                      style={{
                        backgroundColor: hasAccess
                          ? 'var(--color-bg-surface)'
                          : 'var(--color-bg-base)',
                        borderColor: hasAccess
                          ? 'var(--color-border)'
                          : 'var(--color-border-faint)',
                      }}
                    />
                  );
                })}
              </div>

              {/* Empty state for group with no accessible tools */}
              {!hasAccessibleTools && (
                <div
                  className="mt-4 p-4 rounded-lg text-sm"
                  style={{
                    fontFamily: 'Outfit, system-ui, sans-serif',
                    color: 'var(--color-text-dim)',
                    backgroundColor: 'var(--color-bg-elevated)',
                  }}
                >
                  No tools available in this category for your current role.
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Footer hint */}
      <div
        className="max-w-7xl mx-auto mt-16 pt-6 text-center text-sm"
        style={{
          fontFamily: 'Outfit, system-ui, sans-serif',
          color: 'var(--color-text-dim)',
          borderTop: `1px solid var(--color-border-faint)`,
        }}
      >
        Pro tip: Press{' '}
        <kbd
          className="px-2 py-0.5 rounded text-xs"
          style={{
            backgroundColor: 'var(--color-bg-elevated)',
            color: 'var(--color-text-secondary)',
          }}
        >
          ⌘K
        </kbd>{' '}
        (or{' '}
        <kbd
          className="px-2 py-0.5 rounded text-xs"
          style={{
            backgroundColor: 'var(--color-bg-elevated)',
            color: 'var(--color-text-secondary)',
          }}
        >
          Ctrl+K
        </kbd>
        ) anywhere to open the command palette.
      </div>
    </div>
  );
}
